import { Button, Col, Radio, Row } from 'antd';
import axios from 'axios';
import RestGoogleAutocomplete from 'components/RestInput/RestGoogleAutocomplete';
import { RestInputContext } from 'components/RestInput/RestInputContext';
import RestSelect from 'components/RestInput/RestSelect';
import AddressFullForm from 'containers/Centres/components/Form/AddressFullForm';
import React, {
  useContext,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import {
  getAddressCentreToGetGeo,
  isValidAddress,
  splitAddressFromGoogleMap,
} from 'utils/dataUtils';
import { getFilterFromUrl } from 'utils/tools';
import { getCentresOfUser } from 'redux/auth/selectors';
import { useTranslation } from 'react-i18next';
import { CentreNameAddressStyles } from './styles';

interface Props {
  setIsDisableBtn?: (value: boolean) => void;
  isWrongAddress?: boolean;
  setIsWrongAddress?: (value: boolean) => void;
}

interface AddressBenchmarking {
  state?: string;
  postCode?: number;
  city?: string;
  addressL1?: string;
}

const CentreAddressSelectForm = forwardRef(
  ({ setIsDisableBtn, isWrongAddress, setIsWrongAddress }: Props, ref) => {
    useImperativeHandle(ref, () => ({
      resetFullForm: () => {
        setAddressForm(null);
      },
    }));
    const { t } = useTranslation();
    const { form } = useContext(RestInputContext);
    const { search } = useLocation();
    const outsideFilter = getFilterFromUrl(search);
    const { addressL1, city, country, postCode, state } = outsideFilter?.filter;
    const [addressForm, setAddressForm] = useState();

    const [isFullAddress, setIsFullAddress] = useState<boolean>();
    const [value, setValue] = useState(
      localStorage.getItem('optionSelectBenchmarking') || 'centreName',
    );
    const [addressBenchmarking, setAddressBenchmarking] =
      useState<AddressBenchmarking>();

    useEffect(() => {
      form.setFieldsValue({
        addressL1,
        country,
        city,
        postCode,
        state,
      });
    }, []); // eslint-disable-line

    const onChange = (e) => {
      setValue(e?.target?.value);
      localStorage.setItem('optionSelectBenchmarking', e?.target?.value);
      if (value !== e?.target?.value) {
        if (e?.target?.value === 'centreName') {
          form.setFieldsValue({
            displayAddress: null,
            postCode: null,
            state: null,
            city: null,
            addressL1: null,
          });
          setIsWrongAddress(false);
          setIsDisableBtn(false);
        } else {
          form.setFieldsValue({ centreId: null });
        }
      }
    };

    const onSelectAddress = ({ addressObj }) => {
      const addressFields = ['addressL1', 'city', 'postCode', 'state'];
      addressFields.forEach((item) => {
        if (!addressObj[item]) {
          form.setFieldsValue({ item: null });
        }
      });
      setAddressForm(addressObj);
      form.setFieldsValue(addressObj);
      setAddressBenchmarking(addressBenchmarking);
      if (isValidAddress(addressObj)) {
        form.validateFields(['displayAddress']);
      }
    };

    const centresData = useSelector(getCentresOfUser);

    useEffect(() => {
      if (addressBenchmarking) {
        axios(
          `https://maps.googleapis.com/maps/api/geocode/json?components=country:AU&address=${getAddressCentreToGetGeo(
            addressBenchmarking,
          )}&key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`,
        ).then((res) => {
          const addressObj = splitAddressFromGoogleMap(
            res?.data?.results?.[0]?.address_components,
          );

          if (
            addressObj.city?.toLowerCase() ===
              addressBenchmarking.city?.toLowerCase() &&
            addressObj.postCode === addressBenchmarking.postCode &&
            addressObj.state?.toLowerCase() ===
              addressBenchmarking.state?.toLowerCase() &&
            addressObj.addressL1?.toLowerCase() ===
              addressBenchmarking.addressL1?.toLowerCase()
          ) {
            setIsWrongAddress(false);
            setIsDisableBtn(false);
          } else {
            setIsWrongAddress(true);
            setIsDisableBtn(true);
          }
        });
      }
    }, [addressBenchmarking]); // eslint-disable-line

    const tryFullAddressForm = () => {
      setIsFullAddress(true);
      setAddressBenchmarking(addressForm);
      form.setFieldsValue(addressForm);
      if (isWrongAddress) {
        setIsDisableBtn(true);
      }
    };

    return (
      <CentreNameAddressStyles>
        <Radio.Group className="w-full" value={value} onChange={onChange}>
          <Row gutter={32}>
            <Col xl={12} lg={12} md={24} xs={24}>
              <Radio value="centreName">{t('centres.centreName')}</Radio>
              <RestSelect
                required={value !== 'address'}
                source="centreId"
                placeholder={t('centres.centreName')}
                valueProp="id"
                titleProp="name"
                resourceData={centresData || []}
                disabled={value !== 'centreName'}
              />
            </Col>
            <Col xl={12} lg={12} md={24} xs={24}>
              <Radio value="address">{t('centres.address')}</Radio>
              {!isFullAddress && (
                <>
                  <RestGoogleAutocomplete
                    source="displayAddress"
                    placeholder="centres.address"
                    required={value !== 'centreName'}
                    disabled={value !== 'address'}
                    onSelectAddress={onSelectAddress}
                    rules={[
                      ({ getFieldsValue }) => ({
                        validator(_, value) {
                          if (
                            !value ||
                            isValidAddress(
                              getFieldsValue([
                                'addressL1',
                                'city',
                                'postCode',
                                'state',
                              ]),
                            )
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(t('centres.errorDisplayAddress')),
                          );
                        },
                      }),
                    ]}
                  />
                  <div className="flex items-center fill-address">
                    <div className="location-question">
                      {t('centres.notCorrectAddress')}
                    </div>
                    <Button
                      className="address-full-form-button"
                      type="link"
                      onClick={() => tryFullAddressForm()}
                      disabled={value !== 'address'}
                    >
                      {t('button.tryFullForm')}
                    </Button>
                  </div>
                </>
              )}
              <div className={isFullAddress ? '' : 'd-none'}>
                <AddressFullForm
                  form={form}
                  isAutoAddress
                  initialValue={city ? { city } : null}
                  onClick={() => {
                    setIsFullAddress(false);
                    setIsDisableBtn(false);
                  }}
                  setGeoAddressObj={setAddressBenchmarking}
                  disabled={value !== 'address'}
                  required={value !== 'centreName'}
                  isWrongAddress={isWrongAddress}
                />
              </div>
            </Col>
          </Row>
        </Radio.Group>
      </CentreNameAddressStyles>
    );
  },
);

export default CentreAddressSelectForm;
